import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, TrendingUp, HeartHandshake, GraduationCap, Loader2 } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { StaggerGroup, StaggerItem, Reveal } from "@/components/shared/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getJobs, applyJob, formatApiError } from "@/lib/api";

const perks = [
  { icon: TrendingUp, title: "Growth & Training", desc: "Clear career paths and continuous skill development." },
  { icon: HeartHandshake, title: "Supportive Culture", desc: "A respectful, team-first workplace that has your back." },
  { icon: GraduationCap, title: "Learn from the Best", desc: "Work alongside 21+ years of industry expertise." },
];

const emptyApp = { name: "", email: "", phone: "", position: "", experience: "", cover_letter: "" };

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyApp);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    getJobs().then(setJobs).catch(() => setJobs([])).finally(() => setLoading(false));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const applyTo = (title) => {
    setForm((f) => ({ ...f, position: title }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.position) {
      toast.error("Please fill in name, email, phone and position.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await applyJob(form);
      toast.success(res.message || "Application submitted!");
      setForm(emptyApp);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        breadcrumb="Careers"
        eyebrow="Join the movement"
        title="Build your career in motion."
        description="We're always looking for driven people who care about doing things right. Come move India with us."
      />

      {/* Perks */}
      <section className="bg-white py-24 lg:py-32">
        <div className="container-x">
          <SectionHeading align="center" className="mb-16" eyebrow="Why join us" title="More than just a job." />
          <StaggerGroup className="grid md:grid-cols-3 gap-6">
            {perks.map(({ icon: Icon, title, desc }) => (
              <StaggerItem key={title} className="h-full">
                <div className="h-full rounded-3xl border border-gray-100 bg-surface p-8 text-center hover:shadow-card transition-shadow duration-300">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light text-brand"><Icon className="h-7 w-7" /></div>
                  <h3 className="mt-5 font-heading font-bold text-lg text-ink">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Jobs */}
      <section className="bg-surface py-24 lg:py-32">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Open positions" title="Current openings." description="Find a role that fits and apply in minutes." />
          </div>
          <div className="lg:col-span-7">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
            ) : (
              <Accordion type="single" collapsible className="space-y-4" data-testid="jobs-accordion">
                {jobs.map((job) => (
                  <AccordionItem key={job.id} value={job.id} className="rounded-2xl border border-gray-100 bg-white px-6 shadow-soft" data-testid={`job-${job.id}`}>
                    <AccordionTrigger className="hover:no-underline py-5">
                      <div className="flex flex-col items-start text-left">
                        <span className="font-heading font-bold text-lg text-ink">{job.title}</span>
                        <span className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{job.department}</span>
                          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.location}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{job.type}</span>
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <p className="text-muted-foreground leading-relaxed">{job.description}</p>
                      <p className="mt-3 text-sm"><span className="font-semibold text-ink">Experience:</span> <span className="text-muted-foreground">{job.experience}</span></p>
                      <button
                        onClick={() => applyTo(job.title)}
                        data-testid={`apply-${job.id}`}
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-deep transition-colors"
                      >
                        Apply for this role
                      </button>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section ref={formRef} className="bg-white py-24 lg:py-32 scroll-mt-24">
        <div className="container-x max-w-3xl">
          <SectionHeading align="center" className="mb-12" eyebrow="Apply now" title="Send us your application." />
          <Reveal>
            <form onSubmit={handleSubmit} className="rounded-3xl border border-gray-100 bg-surface p-6 sm:p-10 shadow-card" data-testid="application-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="a-name">Full Name *</Label>
                  <Input id="a-name" data-testid="app-name" value={form.name} onChange={set("name")} className="bg-white h-12 rounded-xl" placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="a-email">Email *</Label>
                  <Input id="a-email" type="email" data-testid="app-email" value={form.email} onChange={set("email")} className="bg-white h-12 rounded-xl" placeholder="you@example.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="a-phone">Phone *</Label>
                  <Input id="a-phone" data-testid="app-phone" value={form.phone} onChange={set("phone")} className="bg-white h-12 rounded-xl" placeholder="Mobile number" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="a-position">Position *</Label>
                  <Select value={form.position} onValueChange={set("position")}>
                    <SelectTrigger id="a-position" data-testid="app-position" className="bg-white h-12 rounded-xl"><SelectValue placeholder="Select a role" /></SelectTrigger>
                    <SelectContent>
                      {jobs.map((j) => <SelectItem key={j.id} value={j.title}>{j.title}</SelectItem>)}
                      <SelectItem value="Other / General Application">Other / General Application</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="a-exp">Years of Experience</Label>
                  <Input id="a-exp" data-testid="app-experience" value={form.experience} onChange={set("experience")} className="bg-white h-12 rounded-xl" placeholder="e.g. 3 years" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="a-cover">Cover Letter</Label>
                  <Textarea id="a-cover" data-testid="app-cover" value={form.cover_letter} onChange={set("cover_letter")} className="bg-white rounded-xl min-h-[120px]" placeholder="Tell us why you'd be a great fit..." />
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="app-submit" className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-8 py-4 hover:bg-brand-deep transition-colors disabled:opacity-70">
                {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</> : "Submit Application"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
